import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { get, last, noop } from 'lodash';
import { FocusableParent } from 'components/navigation';
import { StyledBackButton } from './StyledComponents';
import DetailsHeader from './DetailsHeader';
import WatchControls from './WatchControls';
import DetailsDescription from './DetailsDescription';

const SECTION_NAME = 'MAIN_CONTENT_SECTION';

const innerSectionsExtraOffset = [0, 540];

const DESCRIPTION_TEXT_MAX_LENGTH = 350;

const MainContentSection = (props) => {
  const { noAnimation, data, isOnPurchaseRequest, pageOffset, baseFocusOffset } = props;
  const { onWatchControlEnter, onItemFocus } = props;

  const history = useHistory();
  const [selectedSection, setSelectedSection] = useState(0);

  const isExpandDescriptionButtonVisible = useMemo(() => {
    const title = get(data, ['title']);
    const description = get(title, ['summaryLong']) || get(title, ['summaryShort']) || '';
    return description.length > DESCRIPTION_TEXT_MAX_LENGTH;
  }, [data]);

  const onInnerSectionFocus = useCallback(
    (index) => {
      const offset = innerSectionsExtraOffset[index] + baseFocusOffset;
      setSelectedSection(index);
      onItemFocus(offset);
    },
    [baseFocusOffset, onItemFocus],
  );

  const selectFirstSection = useCallback(() => onInnerSectionFocus(0), [onInnerSectionFocus]);
  const selectSecondSection = useCallback(() => onInnerSectionFocus(1), [onInnerSectionFocus]);

  const IsSectionCompletelyScrolled = pageOffset > baseFocusOffset + last(innerSectionsExtraOffset);

  return (
    <FocusableParent sectionName={SECTION_NAME}>
      <StyledBackButton
        onEnter={history.goBack}
        onFocus={selectFirstSection}
        noAnimation={noAnimation}
        buttonOffset={pageOffset}
      />
      <DetailsHeader
        data={data || {}}
        selectedSection={selectedSection}
        IsSectionCompletelyScrolled={IsSectionCompletelyScrolled}
        noAnimation={noAnimation}
        onCoverFocus={selectSecondSection}
        onCoverEnter={data ? onWatchControlEnter : noop}
      />
      <WatchControls
        data={data || {}}
        isOnPurchaseRequest={isOnPurchaseRequest}
        sectionName="WATCH_CONTROLS"
        isVisible={selectedSection === 0}
        isExpandDescriptionButtonVisible={isExpandDescriptionButtonVisible}
        onFocus={selectFirstSection}
        onItemEnter={data ? onWatchControlEnter : noop}
      />
      {data && (
        <DetailsDescription
          data={data}
          maxTextLength={DESCRIPTION_TEXT_MAX_LENGTH}
          isVisible={!IsSectionCompletelyScrolled}
          onFocus={selectSecondSection}
        />
      )}
    </FocusableParent>
  );
};

MainContentSection.propTypes = {
  data: PropTypes.object,
  pageOffset: PropTypes.number.isRequired,
  baseFocusOffset: PropTypes.number.isRequired,
  onWatchControlEnter: PropTypes.func.isRequired,
  onItemFocus: PropTypes.func.isRequired,
  noAnimation: PropTypes.bool,
};

export default MainContentSection;
